import type { StoryView } from '../../../packages/contracts/src/story.ts';
import { postedLabel, storyWays, type StoryContent, type StorySession } from '../../../packages/sim/src/campaign/story.ts';
import { prerequisitesMet } from '../../../packages/sim/src/quests/graph.ts';
import type { Marker } from './world-objects.ts';

export type Lead = StoryView['next'][number];
type Way = StoryView['exits'][number];
export const leadKey = (lead: Lead): string => `${lead.beatId}|${lead.targetId}`;

/** Prefer an open way, even when longer. Otherwise show the shortest visible way and its barriers.
 * Hidden shortcuts never enter this graph. This is an itinerary, never a command queue. */
export function trailTo(content: StoryContent, story: StorySession, to: string): Way[] | null {
  const ways = new Map<string, Way[]>();
  const exits = (from: string): Way[] => {
    if (!ways.has(from)) ways.set(from, storyWays(content, story, from));
    return ways.get(from)!;
  };
  for (const openOnly of [true, false]) {
    const queue: { from: string; path: Way[] }[] = [{ from: story.beatId, path: [] }], seen = new Set([story.beatId]);
    for (let i = 0; i < queue.length; i++) {
      const { from, path } = queue[i]!;
      if (from === to) return path;
      for (const exit of exits(from)) {
        if (seen.has(exit.beatId) || (openOnly && !exit.enabled)) continue;
        seen.add(exit.beatId); queue.push({ from: exit.beatId, path: [...path, exit] });
      }
    }
  }
  return null;
}

/** Locate a lead's subject, or the next exit. A local gate can point to the person who offers passage;
 * the player still chooses their terms in the ordinary conversation. */
export function trailMarker(content: StoryContent, story: StorySession, lead: Lead, trail: Way[] | null, markers: readonly Marker[]): Marker | undefined {
  const first = trail?.[0];
  if (first) {
    if (!first.enabled) {
      const gate = content.routes.find(route => route.fromBeatId === story.beatId && route.toBeatId === first.beatId && route.reason && !prerequisitesMet(route.prerequisites, story.slice.quest.facts));
      const missing = [...gate?.prerequisites.all ?? [], ...gate?.prerequisites.any ?? []].filter(fact => !story.slice.quest.facts.includes(fact));
      const helps = content.actions.filter(action => action.beatId === story.beatId && action.effects.add.some(fact => missing.includes(fact)) && postedLabel(content, story, action.id));
      const helper = markers.find(marker => marker.kind === 'person' && helps.some(action => action.speakerId === marker.id));
      if (helper) return helper;
    }
    return markers.find(marker => marker.kind === 'exit' && marker.id === first.beatId);
  }
  if (!trail) return undefined;
  const action = content.actions.find(action => action.id === lead.targetId);
  const speaker = action?.speakerId ?? markers.find(marker => marker.kind === 'person' && marker.id === lead.targetId)?.id;
  return markers.find(marker => speaker ? marker.kind === 'person' && marker.id === speaker
    : marker.verbs.some(verb => verb.id === lead.targetId) || (lead.targetId === 'still-place' && marker.id === 'shelter'));
}
